// NewProjectHost — the New project box's shell (#213). Every "New project"
// (left menu, Projects page, Cmd+K) opens the `new-project` overlay; this
// renders the box while it is open and runs its ports.
//
// Nothing exists until "Create project": then the project is created with its
// name, what it is about and its audience (its brief, which every assistant
// turn in it reads) and the modules laid out in the box (none: it starts
// empty; never default parts), the box's sources go on its canvas as a tidy
// group to the right, and the Studio opens on it. What it is made for is
// never asked (owner, 31 Oct): the format is chosen at export. Files
// dropped in the box upload at once into Uploaded sources; Create never waits for them — one
// still uploading joins the canvas when it lands. Cancel makes nothing, and
// names the files it uploaded (they stay in Uploaded sources, SF-9).
//
// The draft and the rows are view-ephemeral (gone with the box); the project
// and the uploads are commands. One press of Create makes one project
// (`useOneAtATime`). "Your sources" is a projection of the library read:
// loading until it lands, failed when it could not be read (Try again
// re-reads it). Files holds at most BOX_FILES_MAX: uploads past it are not
// taken (the limit hint names the number).

export { NewProjectHost, placeInGroup, startNewProject };

import { Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useRef, useState } from "react";
import {
    type BoxSource,
    type CreateStatus,
    NewProjectDialog,
    type NewProjectDraft,
} from "../../../components/Projects/NewProjectDialog.js";
import { numberedDefaultName } from "../../createDefaults.js";
import { externalErrorId } from "../../errorId.js";
import { type ExternalError, toDisplay } from "../../externalError.js";
import { classifyQueuedFile, MAX_SOURCE_BYTES } from "../../ingest.js";
import { emptyModules, modulesToCreate } from "../../newProjectModules.js";
import type { ProjectPorts, ProjectStart } from "../../ports/project.js";
import type { PortResult } from "../../ports/result.js";
import {
    BOX_FILES_MAX,
    sourceKindOf,
    type YourSourcesState,
} from "../../yourSources.js";
import { useAppShell } from "../context.js";
import { useOneAtATime } from "./useOneAtATime.js";
import { useRecentList } from "./useRecentList.js";

// A new project's modules lie at the canvas origin (the app puts them
// there) and are 360 wide; the box's sources line up to their
// right, one card (260) and a gap apart, in the box's order.
const BESIDE_STARTING_MODULES = 360 + 96;
const GROUP_STEP = 260 + 32;
const placeInGroup = (slot: number): { x: number; y: number } => ({
    x: BESIDE_STARTING_MODULES + slot * GROUP_STEP,
    y: 0,
});

type NewProject = {
    readonly title: string;
    readonly start: ProjectStart;
    readonly description: string;
    readonly audience: string | null;
    // The sources known now, by their slot in the group.
    readonly sources: readonly (readonly [string, number])[];
};

// Create the project with its brief, then lay its sources on its canvas one
// after another (each a command on the same project). The new project's id.
const startNewProject = async (
    ports: Pick<ProjectPorts, "createProject" | "placeOnProjectCanvas">,
    p: NewProject,
): PortResult<string> => {
    const r = await ports.createProject(p.title, p.start, {
        ...(p.description === "" ? {} : { description: p.description }),
        ...(p.audience === null ? {} : { audiences: [p.audience] }),
    });
    if (Result.isErr(r)) return r;
    await p.sources.reduce(
        (done: Promise<unknown>, [id, slot]) =>
            done.then(() =>
                ports.placeOnProjectCanvas(r.value, id, placeInGroup(slot)),
            ),
        Promise.resolve(),
    );
    return r;
};

const emptyDraft: NewProjectDraft = {
    name: "",
    about: "",
    modules: emptyModules,
    audience: null,
};

const NewProjectHost = (): ReactNode => {
    const { frame } = useAppShell();
    // Mounted only while open, so every opening starts from an empty box.
    return frame.overlay.type === "new-project" ? <NewProjectBox /> : null;
};

const NewProjectBox = (): ReactNode => {
    const { t } = useLingui();
    const {
        ports,
        library,
        libraryError,
        setLibrary,
        setRetrySeq,
        dispatch,
        showNotice,
        health,
        learningContexts,
    } = useAppShell();
    const [, recent] = useRecentList("projects");
    const [draft, setDraft] = useState<NewProjectDraft>(emptyDraft);
    const [sources, setSources] = useState<readonly BoxSource[]>([]);
    const [failure, setFailure] = useState<ExternalError | null>(null);
    // The box's own upload ordinals, the uploads that have landed (row key →
    // source id), and, once created, the project and each row's slot: an
    // upload landing after Create joins the canvas there.
    const nextUpload = useRef(0);
    const landed = useRef(new Map<string, string>());
    const created = useRef<{
        readonly projectId: string;
        readonly slots: ReadonlyMap<string, number>;
    } | null>(null);

    // The default name: the lowest free "Untitled project {n}" among every
    // project, archived ones included (an untitled one reads as the word).
    const word = t`Untitled project`;
    const defaultName = numberedDefaultName(
        (library?.projects ?? []).map((p) => (p.title === "" ? word : p.title)),
        word,
    );

    const reload = async () => {
        const lib = await ports.loadLibrary();
        if (Result.isOk(lib)) setLibrary(lib.value);
    };
    const settle = (key: string, row: BoxSource) =>
        setSources((rows) => rows.map((s) => (s.key === key ? row : s)));

    const uploadOne = async (file: File) => {
        const key = `upload-${nextUpload.current}`;
        nextUpload.current += 1;
        const title = file.name;
        // Over the size ceiling: an ordinary "couldn't read" row, nothing sent.
        if (classifyQueuedFile(file.size) !== null) {
            setSources((rows) => [...rows, { type: "unreadable", key, title }]);
            return;
        }
        setSources((rows) => [...rows, { type: "uploading", key, title }]);
        const r = await ports.addSource({ file });
        if (Result.isErr(r)) {
            settle(key, { type: "unreadable", key, title });
            return;
        }
        landed.current.set(key, r.value.sourceId);
        settle(key, {
            type: "ready",
            key,
            sourceId: r.value.sourceId,
            title: r.value.title,
            origin: "uploaded",
        });
        const project = created.current;
        const slot = project?.slots.get(key);
        if (project !== null && slot !== undefined)
            await ports.placeOnProjectCanvas(
                project.projectId,
                r.value.sourceId,
                placeInGroup(slot),
            );
        await reload();
    };

    const create = useOneAtATime(async () => {
        setFailure(null);
        const name = draft.name.trim();
        const rows = sources.filter((s) => s.type !== "unreadable");
        const slots = new Map(rows.map((s, i) => [s.key, i]));
        // A ready row carries its id; an upload's is known once landed.
        const idOf = (s: BoxSource): string | undefined =>
            s.type === "ready" ? s.sourceId : landed.current.get(s.key);
        const known = new Map(
            rows.flatMap((s): (readonly [string, string])[] => {
                const id = idOf(s);
                return id === undefined ? [] : [[s.key, id]];
            }),
        );
        const r = await startNewProject(ports, {
            title: name === "" ? defaultName : name,
            start: { modules: modulesToCreate(draft.modules) },
            description: draft.about.trim(),
            audience: draft.audience,
            sources: [...known].map(([key, id]) => [id, slots.get(key) ?? 0]),
        });
        if (Result.isErr(r)) {
            setFailure(r.error);
            return;
        }
        created.current = { projectId: r.value, slots };
        // Uploads that landed while the project was being made (later ones
        // place themselves as they land).
        await rows
            .filter((s) => !known.has(s.key))
            .reduce((done: Promise<unknown>, s) => {
                const id = landed.current.get(s.key);
                const slot = slots.get(s.key);
                return id === undefined || slot === undefined
                    ? done
                    : done.then(() =>
                          ports.placeOnProjectCanvas(
                              r.value,
                              id,
                              placeInGroup(slot),
                          ),
                      );
            }, Promise.resolve());
        await reload();
        recent({ type: "opened", id: r.value });
        dispatch({ type: "close-overlay" });
        dispatch({ type: "open-studio", projectId: r.value });
    });

    const cancel = () => {
        dispatch({ type: "close-overlay" });
        const kept = sources.flatMap((s) =>
            s.type === "uploading" ||
            (s.type === "ready" && s.origin === "uploaded")
                ? [s.title]
                : [],
        );
        if (kept.length > 0) showNotice({ type: "sources-kept", titles: kept });
    };

    const yourSources: YourSourcesState =
        library !== null
            ? {
                  type: "ready",
                  sources: library.sources.map((s) => ({
                      id: s.id,
                      title: s.displayTitle ?? s.title ?? s.id,
                      kind: sourceKindOf(s.title ?? ""),
                  })),
              }
            : libraryError !== null
              ? { type: "failed" }
              : { type: "loading" };

    const status: CreateStatus =
        create === null
            ? { type: "creating" }
            : health === "offline"
              ? { type: "offline" }
              : failure !== null
                ? {
                      type: "failed",
                      title: t(toDisplay(failure).title),
                      body: ((d) => (d === undefined ? undefined : t(d)))(
                          toDisplay(failure).body,
                      ),
                      errorId: externalErrorId(failure),
                  }
                : { type: "ready" };

    return (
        <NewProjectDialog
            open
            draft={draft}
            onDraftChange={setDraft}
            defaultName={defaultName}
            audiences={learningContexts}
            sources={sources}
            yourSources={yourSources}
            onPick={(s) =>
                setSources((rows) =>
                    rows.some((r) => r.type === "ready" && r.sourceId === s.id)
                        ? rows.filter(
                              (r) =>
                                  !(r.type === "ready" && r.sourceId === s.id),
                          )
                        : [
                              ...rows,
                              {
                                  type: "ready",
                                  key: s.id,
                                  sourceId: s.id,
                                  title: s.title,
                                  origin: "picked",
                              },
                          ],
                )
            }
            onRetrySources={() => setRetrySeq((n) => n + 1)}
            onShowAllSources={() => {
                cancel();
                dispatch({ type: "open-knowledge" });
            }}
            onUpload={(files) => {
                const taken = sources.filter(
                    (s) => s.type !== "unreadable",
                ).length;
                files
                    .slice(0, Math.max(0, BOX_FILES_MAX - taken))
                    .map(uploadOne);
            }}
            onRemove={(key) =>
                setSources((rows) => rows.filter((s) => s.key !== key))
            }
            fileLimit={`${MAX_SOURCE_BYTES / 1_000_000} MB`}
            status={status}
            onCreate={() => create?.()}
            onCancel={cancel}
        />
    );
};
